import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navbar from "../Navbar/Main";
import Footer from "../Footer/Main";
import "./imfdetails.css";
import ShareToolsBar from './ShareToolsBar';
import PageFeedbackCard from './PageFeedbackCard';

const speakers = [
  { name: "Soraya Hakuziyaremye", title: "Governor", image: "/images/soraya.png" },
  { name: "Bertrand Ndengeyingoma", title: "CTIO, BNR", image: "/images/ED-Bertrand.jpg" },
  { name: "Kasangwa Chantal", title: "ED OPD, BNR", image: "/images/ED-Chantal.jpg" },
  { name: "Ihogoza France", title: "ED Finance", image: "/images/ED-Frances.jpg" },
  { name: "Dushime", title: "Board Member", image: "/images/dushime.png" },
];
const highlights = [
  {
    heading: "Why This Conversation Matters?",
    body: [
      "Cross-border interoperability is now central to Africa's digital financial agenda. It is not only about moving funds, but also about reducing costs, increasing inclusion, and enabling secure innovation.",
      "The discussion explored practical enablers including API standards, legal harmonization, digital identity, and public-private governance for payment rails.",
    ],
  },
  {
    heading: "What Exists Today: Strong Foundations, Limited Integration",
    body: [
      "Real-time payment systems are increasingly available in African markets, yet integration between them remains limited.",
      "Participants emphasized that the next milestone is regional connectivity and user-centered design, so businesses and citizens can transact across borders without friction.",
    ],
  },
  {
    heading: "What is Blocking Interoperability?",
    body: [
      "Fragmented regulations, inconsistent technical standards, and uneven readiness across jurisdictions still create barriers.",
      "The meeting underlined the need for coordinated implementation plans with strong institutional ownership and measurable milestones.",
    ],
  },
  {
    heading: "Looking From Global Experience",
    body: [
      "Case studies from other regions showed that interoperable systems scale faster when market incentives align with public policy goals.",
      "This reinforces the role of central banks in convening market actors, supervising risk, and enabling trust frameworks.",
    ],
  },
  {
    heading: "Looking Ahead: Pathways for the Next 3-5 Years",
    body: [
      "A phased roadmap is recommended: strengthen domestic rails, establish common principles, pilot bilateral corridors, and scale to multilateral platforms.",
      "Success will depend on sustained collaboration between regulators, payment service providers, and technology partners.",
    ],
  },
];

const getYouTubeVideoId = (value) => {
  if (!value || typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const directId = trimmed.match(/^[a-zA-Z0-9_-]{11}$/);
  if (directId) return directId[0];

  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const shortId = url.pathname.split("/").filter(Boolean)[0];
      return shortId && shortId.length === 11 ? shortId : null;
    }

    if (host.includes("youtube.com")) {
      const fromQuery = url.searchParams.get("v");
      if (fromQuery && fromQuery.length === 11) return fromQuery;

      const pathParts = url.pathname.split("/").filter(Boolean);
      const embedIndex = pathParts.findIndex((part) => part === "embed" || part === "shorts");
      if (embedIndex >= 0 && pathParts[embedIndex + 1]) {
        const candidate = pathParts[embedIndex + 1];
        return candidate.length === 11 ? candidate : null;
      }
    }
  } catch (error) {
    return null;
  }

  return null;
};

const getItemUrlCandidate = (item) => {
  if (!item || typeof item !== "object") return "";
  return item.youtube_url || item.video_url || item.url || item.link || item.href || item.path || "";
};

const ImfDetails = () => {
  const [links, setLinks] = useState([]);
  const [publications, setPublications] = useState([]);
  const galleryRef = useRef(null);
  const speakersRef = useRef(null);

  useEffect(() => {
    axios
      .get("/links")
      .then((response) => {
        setLinks(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Failed to load /links:", error);
        setLinks([]);
      });
  }, []);

  useEffect(() => {
    axios
      .get("/publications")
      .then((response) => {
        setPublications(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Failed to load /publications:", error);
        setPublications([]);
      });
  }, []);

  const videos = useMemo(() => {
    return links
      .map((item, index) => {
        const url = getItemUrlCandidate(item);
        const videoId = getYouTubeVideoId(url);
        if (!videoId) return null;
        return {
          id: item.id ?? index,
          title: item.title || item.keyword || `Video ${index + 1}`,
          embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0&playsinline=1`,
        };
      })
      .filter(Boolean);
  }, [links]);

  const scrollGallery = (direction) => {
    if (!galleryRef.current) return;
    const amount = direction === "left" ? -340 : 340;
    galleryRef.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  const scrollSpeakers = (direction) => {
    if (!speakersRef.current) return;
    const amount = direction === "left" ? -240 : 240;
    speakersRef.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <>
      <Navbar />
      <div className="stricky-header stricked-menu main-menu">
        <div className="sticky-header__content"></div>
      </div>

      <section className="page-header">
        <div className="page-header__bg"></div>
        <div className="container">
          <ul className="thm-breadcrumb list-unstyled">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/Publications">Publications</Link></li>
            <li><span>IMF Details</span></li>
          </ul>
        </div>

      </section>

      <section className="imf-details">
        <div className="container">
          <div className="row row-gutter-y-30">
            <div className="col-lg-12">
              <article className="imf-details__paper">
                <div className="d-flex justify-content-end mb-3">
                  <ShareToolsBar pageTitle="IMF Details — National Bank of Rwanda" />
                </div>

                <h1 className="imf-details__title">
                  Strengthening Africa&apos;s Financial Future: Key Highlights from the IMF-Central Bank Meeting in Kigali
                </h1>

                <div className="imf-details__meta">
                  <span>Monday, May 2024</span>
                  <span>News</span>
                </div>

                <p>
                  On May 13, 2024, senior leaders in financial services gathered in Kigali to discuss practical steps toward a more connected African payments ecosystem.
                  The session focused on digital payments, interoperability, and policy alignment required for inclusive growth.
                </p>
                <p>
                  Hosted by the National Bank of Rwanda with regional and global partners, the event brought together regulators, technologists,
                  and market operators to exchange lessons and define priorities for implementation.
                </p>

                <section className="imf-details__section">
                  <h2>At the Speakers</h2>
                  <div className="imf-details__speakers-wrap">
                    <button
                      type="button"
                      className="imf-details__gallery-arrow imf-details__gallery-arrow--left"
                      aria-label="Scroll speakers left"
                      onClick={() => scrollSpeakers("left")}
                    >
                      <i className="fa fa-angle-left" />
                    </button>
                    <div className="imf-details__speakers" ref={speakersRef}>
                      {speakers.map((speaker) => (
                        <div className="imf-details__speaker" key={speaker.name}>
                          <img src={speaker.image} alt={speaker.name} />
                          <div className="imf-details__speaker-info">
                            <strong>{speaker.name}</strong>
                            <span>{speaker.title}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="imf-details__gallery-arrow imf-details__gallery-arrow--right"
                      aria-label="Scroll speakers right"
                      onClick={() => scrollSpeakers("right")}
                    >
                      <i className="fa fa-angle-right" />
                    </button>
                  </div>
                </section>

                {highlights.map((item) => (
                  <section className="imf-details__split" key={item.heading}>
                    <h3>{item.heading}</h3>
                    <div>
                      {item.body.map((paragraph, idx) => (
                        <p key={`${item.heading}-${idx}`}>{paragraph}</p>
                      ))}
                    </div>
                  </section>
                ))}

                <div className="imf-details__gallery-wrap">
                  <button
                    type="button"
                    className="imf-details__gallery-arrow imf-details__gallery-arrow--left"
                    aria-label="Scroll videos left"
                    onClick={() => scrollGallery("left")}
                  >
                    <i className="fa fa-angle-left" />
                  </button>

                  <div className="imf-details__gallery" ref={galleryRef}>
                    {videos.map((video) => (
                      <div className="imf-details__video-tile" key={video.id}>
                        <iframe
                          src={video.embedUrl}
                          title={video.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        />
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="imf-details__gallery-arrow imf-details__gallery-arrow--right"
                    aria-label="Scroll videos right"
                    onClick={() => scrollGallery("right")}
                  >
                    <i className="fa fa-angle-right" />
                  </button>
                </div>

                <p className="imf-details__closing">
                  Africa is not starting from zero. The continent already has strong innovation capacity and active policy leadership.
                  With coordinated execution and ecosystem partnerships, interoperable payments can become a major accelerator for trade,
                  financial inclusion, and resilience.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="service-five publications-bottom-section">
        <div className="container">
          <div className="row row-gutter-y-30">
            <div className="col-lg-8">
              <div className="service-card">
                <div className="service-card__content">
                  <div className="service-card__content__inner">
                    <h3 className="service-card__title mb-3">Related pages in this section</h3>
                    <div className="row">
                      {publications.map((p) => (
                        <div className="col-md-6 mb-2" key={`all-${p.id}`}>
                          <Link to={p.link}>
                            <i className="fa fa-angle-right"></i> {p.title}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <PageFeedbackCard pagePath="/imfdetails" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ImfDetails;
